import React, { Component } from 'react';
import PropTypes from 'prop-types';

class ValidationMessage extends Component {
  static propTypes = {
    type: PropTypes.oneOf(['error', 'warning', 'success']).isRequired,
    messages: PropTypes.array,
    showTooltip: PropTypes.bool,
    show: PropTypes.bool,
  };

  render() {
    const { show, messages, type, showTooltip } = this.props;

    const showClassName = showTooltip ? ` input__${type}--show-tooltip` : "";

    if (show && !!messages) {
      return <small className={`input__${type}${showClassName}`}>{messages[0]}</small>;
    }

    return null;
  }
}

class ValidationError extends ValidationMessage {
  static defaultProps = {
    type: 'error',
  };
}

class ValidationWarning extends ValidationMessage {
  static defaultProps = {
    type: 'warning',
  };
}

class ValidationSuccess extends ValidationMessage {
  static defaultProps = {
    type: 'success',
  };
}

/**
 * Pull out the validation messages and variables.
 * This should be applied to an objects prototype.
 *
 * @author Sam Sehnert <sam@customd.com>
 *
 * @return {Object}  Pull out validation messages from the fieldApi
 */
const getValidation = ({ props, state }) => {
  const {
    fieldApi: { getTouched, getError, getWarning, getSuccess },
  } = props;
  const { showTooltip } = state;

  const touched = getTouched();
  const error = getError();
  const warning = getWarning();
  const success = getSuccess();

  return {
    touched,
    error,
    warning,
    success,
    showTooltip,
  };
};

/**
 * Work out props for error validation component.
 * This should be applied to an objects prototype.
 *
 * @author Sam Sehnert <sam@customd.com>
 *
 * @return {Object} The props to use for the error validation component.
 */
const getErrorProps = ({ props, state }) => {
  const { touched, error, showTooltip } = getValidation({ props, state });

  return {
    show: !!touched,
    messages: error,
    showTooltip,
  };
};

/**
 * Work out props for warning validation component.
 * This should be applied to an objects prototype.
 *
 * @author Sam Sehnert <sam@customd.com>
 *
 * @return {Object} The props to use for the warning validation component.
 */
const getWarningProps = ({ props, state }) => {
  const { touched, error, warning, showTooltip } = getValidation({ props, state });

  return {
    show: !!(touched && !error),
    messages: warning,
    showTooltip,
  };
};

/**
 * Work out props for success validation component.
 * This should be applied to an objects prototype.
 *
 * @author Sam Sehnert <sam@customd.com>
 *
 * @return {Object} The props to use for the success validation component.
 */
const getSuccessProps = ({ props, state }) => {
  const { touched, error, warning, success, showTooltip } = getValidation({ props, state });

  return {
    show: !!(touched && !error && !warning),
    messages: success,
    showTooltip,
  };
};

export { ValidationError, ValidationWarning, ValidationSuccess, getErrorProps, getWarningProps, getSuccessProps };

export default ValidationMessage;
